/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect } from 'react';
import CarsUnder10kHero from './components/CarsUnder10kHero';
import FeaturedVehicles from './components/FeaturedVehicles';
import ValueProposition from './components/ValueProposition';
import TrustSignals from '../../components/feature/TrustSignals';
import ServiceAreaStrip from './components/ServiceAreaStrip';
import CarsUnder10kFaq from './components/CarsUnder10kFaq';
import CarsUnder10kCta from './components/CarsUnder10kCta';
import { useSEOHead } from '../../hooks/useSEOHead';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import { pageFaqs } from '../../mocks/pageFaqs';

const OG_IMAGE = 'https://public.readdy.ai/ai/img_res/f9fdb05a2aa2c7a0e2448a559a994162.jpg';

export default function CarsUnder10kPage() {
  useSEOHead({
    title: 'Cars Under $10K Oregon Coast | Warrenton Astoria Seaside',
    description: 'Quality inspected used cars under $10K in Warrenton, Astoria, Seaside, Tillamook, Lincoln City, Newport & SW Washington. Transparent pricing, financing available.',
    keywords: 'cars under 10000, cheap cars Oregon, affordable used cars, budget cars Warrenton, used cars under 10k Astoria, cheap cars Seaside, affordable vehicles Oregon Coast, budget friendly cars SW Washington',
    canonicalUrl: 'https://rockhoundauto.com/cars-under-10k',
    ogImage: OG_IMAGE,
    geo: {
      region: 'US-OR',
      placename: 'Warrenton, Oregon',
      position: '46.1651;-123.9237',
    },
  });

  // WebPage + ItemList schemas (breadcrumb handled separately below)
  useEffect(() => {
    injectJsonLd('schema-cars-under-10k-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://rockhoundauto.com/cars-under-10k',
      'url': 'https://rockhoundauto.com/cars-under-10k',
      'name': 'Cars Under $10,000 Oregon Coast | Warrenton Astoria Seaside',
      'description': 'Quality inspected used cars under $10K in Warrenton, Astoria, Seaside, Tillamook, Lincoln City, Newport & SW Washington. Transparent pricing, financing available, coast-wide delivery.',
      'inLanguage': 'en-US',
      'isPartOf': { '@type': 'WebSite', 'name': 'Rockhound Auto', 'url': 'https://rockhoundauto.com' },
    });

    injectJsonLd('schema-cars-under-10k-itemlist', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'itemListElement': [
        { '@type': 'Product', 'position': 1, 'name': '2016 Honda Civic', 'offers': { '@type': 'Offer', 'price': '8995', 'priceCurrency': 'USD' } },
        { '@type': 'Product', 'position': 2, 'name': '2015 Toyota Corolla', 'offers': { '@type': 'Offer', 'price': '7995', 'priceCurrency': 'USD' } },
        { '@type': 'Product', 'position': 3, 'name': '2015 Ford Escape', 'offers': { '@type': 'Offer', 'price': '9495', 'priceCurrency': 'USD' } },
      ],
    });

    return () => {
      removeJsonLd('schema-cars-under-10k-webpage');
      removeJsonLd('schema-cars-under-10k-itemlist');
    };
  }, []);

  useEffect(() => {
    injectJsonLd('schema-under-10k-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rockhoundauto.com/' },
        { '@type': 'ListItem', position: 2, name: 'Cars Under $10k', item: 'https://rockhoundauto.com/cars-under-10k' },
      ],
    });
    return () => {
      removeJsonLd('schema-under-10k-breadcrumb');
    };
  }, []);

  useEffect(() => {
    // Inject FAQPage schema
    injectFAQSchema(
      'schema-cars-under-10k-faq',
      pageFaqs.carsUnder10k,
      'https://rockhoundauto.com/cars-under-10k#faqpage'
    );

    return () => {
      removeFAQSchema('schema-cars-under-10k-faq');
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Cars Under $10k' }]} />
      <CarsUnder10kHero />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Dealer Insight Block */}
        <div className="mb-16 bg-gradient-to-br from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Dealer Insight</h3>
              <p className="text-gray-700 leading-relaxed">
                Budget vehicles under $10,000 are where dealer integrity matters most. Many lots sell problem cars to first-time buyers who don't know better. We inspect everything and disclose issues upfront—no surprises after purchase. Our under-$10k inventory turns over fast because buyers trust we're not dumping junk on them.
              </p>
            </div>
          </div>
        </div>
        <TrustSignals variant="features" />
        <FeaturedVehicles />
        <ValueProposition />
        <ServiceAreaStrip />
        <CarsUnder10kFaq />
        <CarsUnder10kCta />
      </div>
    </>
  );
}